import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { documentsList } from "../../store/actions/documentsActions";
import { fetchPriceApparat } from "../../store/actions/paymentsActions";
import FileTypeIcon from "../UI/elements/FileTypeIcon";
import { useKioskToast, KioskToast } from "../UI/elements/useKioskToast";

const MAX_SELECTED = 3;
const FALLBACK_BLANK_PRICE = 30; // пока priceApparat ещё не загрузился

const blockVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, delay: i * 0.1 },
    }),
};

function ListDoc() {
    const dispatch = useDispatch();
    const location = useLocation();
    const { documentsDatas, loading, error } = useSelector(
        (state) => state.documentsList,
    );
    const { priceApparat } = useSelector((state) => state.payments);
    const { t } = useTranslation();

    const [filter, setFilter] = useState("new"); // "new" | "old"
    const [selectedIds, setSelectedIds] = useState([]);
    const { toastMsg, showToast } = useKioskToast();

    useEffect(() => {
        dispatch(documentsList());
        dispatch(fetchPriceApparat());
    }, [dispatch]);

    const handleSelectDoc = (id) => {
        setSelectedIds((prev) => {
            if (prev.includes(id)) {
                return prev.filter((itemId) => itemId !== id);
            }
            if (prev.length >= MAX_SELECTED) {
                showToast(t("listDoc.toast_max"));
                return prev;
            }
            return [...prev, id];
        });
    };

    const sortedDocuments = documentsDatas
        ? [...documentsDatas].sort((a, b) =>
              filter === "new"
                  ? new Date(b.created_at) - new Date(a.created_at)
                  : new Date(a.created_at) - new Date(b.created_at),
          )
        : [];

    const blankDisabled = selectedIds.length > 0;

    return (
        <div className="h-[88vh] px-[2.4vw] py-[2vh] flex flex-col gap-[1.8vh] bg-[#EDEFF4]">
            {/* Заголовок, фильтр по дате, обновление */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={0}
                className="flex-none flex items-center gap-[1.4vw]"
            >
                <h2 className="text-[2.8vh] font-extrabold tracking-tight mr-auto">
                    {t("listDoc.title")}
                </h2>
                <div className="flex bg-[#F1F3F7] rounded-full p-[0.4vh]">
                    <button
                        onClick={() => setFilter("new")}
                        className={`px-[1.8vw] py-[0.8vh] rounded-full text-[1.5vh] font-bold transition-colors ${
                            filter === "new"
                                ? "bg-[#12161F] text-white"
                                : "text-[#7B8290]"
                        }`}
                    >
                        {t("listDoc.filter_new")}
                    </button>
                    <button
                        onClick={() => setFilter("old")}
                        className={`px-[1.8vw] py-[0.8vh] rounded-full text-[1.5vh] font-bold transition-colors ${
                            filter === "old"
                                ? "bg-[#12161F] text-white"
                                : "text-[#7B8290]"
                        }`}
                    >
                        {t("listDoc.filter_old")}
                    </button>
                </div>
                <button
                    onClick={() => dispatch(documentsList())}
                    disabled={loading}
                    className="flex-none flex items-center gap-[0.6vw] bg-[#F1F3F7] text-[#12161F] rounded-full py-[1vh] px-[2vw] text-[1.5vh] font-bold disabled:opacity-60"
                >
                    {t("listDoc.refresh")}
                    <svg
                        className="w-[1.8vh] h-[1.8vh]"
                        style={loading ? { animation: "spin 0.8s linear infinite" } : undefined}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
                    </svg>
                </button>
            </motion.div>

            {/* Памятка про лимит в 3 документа */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={1}
                className="flex-none bg-[#E3ECFB] text-[#246CE2] rounded-[1.6vh] py-[1.2vh] px-[1.8vw] text-[1.4vh] font-bold flex items-center justify-center gap-[1vw]"
            >
                <svg
                    className="w-[2vh] h-[2vh] flex-none"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v5M12 16.5v.01" />
                </svg>
                {t("listDoc.pill_note")}
            </motion.div>

            {/* Сетка файлов */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={2}
                className="flex-1 min-h-0 overflow-y-auto grid grid-cols-2 gap-[1.4vh] content-start"
            >
                {/* Чистый лист A4 — отдельная услуга, не участвует в выборе документов */}
                <Link
                    to={blankDisabled ? "#" : "/printing/blank_sheet"}
                    state={{ from: "/printing/show_list/user" }}
                    onClick={(e) => {
                        if (blankDisabled) e.preventDefault();
                    }}
                    aria-disabled={blankDisabled}
                    className={`bg-[#FAFCFF] border-[0.25vh] border-dashed border-[#C6D6F5] rounded-[2vh] p-[1.6vh_1.4vw] flex items-center gap-[1.2vw] transition-opacity ${
                        blankDisabled
                            ? "opacity-40 pointer-events-none"
                            : "opacity-100"
                    }`}
                >
                    <div className="flex-none w-[4.4vh] h-[4.4vh] flex items-center justify-center">
                        <svg
                            className="w-[3.4vh] h-[4vh]"
                            viewBox="0 0 40 48"
                            fill="none"
                        >
                            <rect
                                x="4"
                                y="3"
                                width="32"
                                height="42"
                                rx="3"
                                fill="#fff"
                                stroke="#246CE2"
                                strokeWidth="2.6"
                                strokeDasharray="5 4"
                            />
                            <path
                                d="M20 17v14M13 24h14"
                                stroke="#246CE2"
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[1.5vh] font-bold truncate">
                            {t("listDoc.blank_name")}
                        </div>
                        <div className="text-[1.15vh] font-semibold text-[#7B8290] mt-[0.4vh] flex items-center gap-[0.6vw] flex-wrap">
                            {t("listDoc.blank_price", {
                                price:
                                    priceApparat?.price_for_blank_sheet ??
                                    FALLBACK_BLANK_PRICE,
                            })}
                            <span className="bg-[#E3ECFB] text-[#246CE2] rounded-full px-[1vw] py-[0.2vh]">
                                {t("listDoc.blank_chip")}
                            </span>
                        </div>
                    </div>
                    <div className="flex-none w-[3.2vh] h-[3.2vh] rounded-full bg-[#246CE2] flex items-center justify-center">
                        <svg
                            className="w-[1.5vh] h-[1.5vh]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                    </div>
                </Link>

                {loading && sortedDocuments.length === 0 && (
                    <div className="col-span-2 flex flex-col items-center justify-center gap-[1.2vh] py-[4vh]">
                        <svg
                            className="w-[3.6vh] h-[3.6vh]"
                            style={{ animation: "spin 0.8s linear infinite" }}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#246CE2"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                        >
                            <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
                        </svg>
                        <p className="text-[1.6vh] font-semibold text-[#7B8290]">
                            {t("listDoc.loading")}
                        </p>
                    </div>
                )}

                {error && (
                    <div className="col-span-2 text-center text-[1.6vh] font-semibold text-[#C2543E] py-[4vh]">
                        {t("listDoc.error")}:{" "}
                        {typeof error === "object"
                            ? error.detail ||
                              error.message ||
                              t("listDoc.error_default")
                            : error}
                    </div>
                )}

                {!loading &&
                    !error &&
                    (!documentsDatas || documentsDatas.length === 0) && (
                        <div className="col-span-2 text-center py-[4vh]">
                            <p className="text-[1.8vh] font-bold">
                                {t("listDoc.no_documents.message")}
                            </p>
                            <p className="text-[1.4vh] font-semibold text-[#7B8290] mt-[0.8vh]">
                                {t("listDoc.no_documents.send")}
                            </p>
                        </div>
                    )}

                {!error &&
                    sortedDocuments.map((doc) => {
                        const isSelected = selectedIds.includes(doc.id);
                        const extension = doc.file_name
                            .split(".")
                            .pop()
                            .toLowerCase();
                        const displayName = doc.file_name;

                        const createdAt = new Date(doc.created_at);
                        const diffMs = Date.now() - createdAt;
                        const minutes = Math.floor(diffMs / 60000);
                        const hours = Math.floor(minutes / 60);
                        const days = Math.floor(hours / 24);
                        const timeAgo =
                            days > 0
                                ? t("docItems.time_ago.days", { count: days })
                                : hours > 0
                                  ? t("docItems.time_ago.hours", { count: hours })
                                  : minutes > 0
                                    ? t("docItems.time_ago.minutes", {
                                          count: minutes,
                                      })
                                    : t("docItems.time_ago.seconds", {
                                          count: Math.floor(diffMs / 1000),
                                      });

                        const statusIsPrinted = doc.status === "printed";
                        const statusIsError =
                            doc.status === "conversion_failed";
                        const statusLabel = statusIsError
                            ? t("docItems.status.conversion_failed")
                            : statusIsPrinted
                              ? t("docItems.status.printed")
                              : t("docItems.status.ready");

                        return (
                            <button
                                key={doc.id}
                                type="button"
                                onClick={() => handleSelectDoc(doc.id)}
                                className={`bg-white rounded-[2vh] p-[1.6vh_1.4vw] flex items-center gap-[1.2vw] text-left border-[0.25vh] transition-colors active:bg-[#F8FAFD] ${
                                    isSelected
                                        ? "border-[#246CE2] bg-[#FBFCFE]"
                                        : "border-transparent shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)]"
                                }`}
                            >
                                <FileTypeIcon
                                    extension={extension}
                                    className="flex-none w-[3.4vh] h-[4vh]"
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[1.5vh] font-bold truncate">
                                        {displayName}
                                    </div>
                                    <div className="text-[1.15vh] font-semibold text-[#7B8290] mt-[0.4vh] flex items-center gap-[0.6vw] flex-wrap">
                                        {timeAgo}
                                        <span
                                            className={`rounded-full px-[1vw] py-[0.2vh] ${
                                                statusIsError
                                                    ? "bg-[#F7E1DC] text-[#C2543E]"
                                                    : statusIsPrinted
                                                      ? "bg-[#E3ECFB] text-[#246CE2]"
                                                      : "bg-[#F1F3F7] text-[#7B8290]"
                                            }`}
                                        >
                                            {statusLabel}
                                        </span>
                                    </div>
                                </div>
                                <div
                                    className={`flex-none w-[2.7vh] h-[2.7vh] rounded-full border-[0.2vh] flex items-center justify-center transition-colors ${
                                        isSelected
                                            ? "bg-[#246CE2] border-[#246CE2]"
                                            : "border-[#E1E4EB]"
                                    }`}
                                >
                                    <AnimatePresence>
                                        {isSelected && (
                                            <motion.svg
                                                initial={{ scale: 0, opacity: 0 }}
                                                animate={{ scale: 1, opacity: 1 }}
                                                exit={{ scale: 0, opacity: 0 }}
                                                transition={{ duration: 0.15 }}
                                                className="w-[1.3vh] h-[1.3vh]"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="#fff"
                                                strokeWidth="3"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M4 12.5l5 5L20 6.5" />
                                            </motion.svg>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </button>
                        );
                    })}
            </motion.div>

            {/* Счётчик выбранных и переход к настройкам печати */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={3}
                className="flex-none relative bg-[#12161F] text-white rounded-[2.2vh] py-[1.4vh] px-[2.4vw] flex items-center gap-[1.6vw]"
            >
                <KioskToast message={toastMsg} position="bottom" />

                <span className="text-[1.7vh] font-bold">
                    {t("listDoc.selected_of", { count: selectedIds.length })}
                </span>

                <Link
                    to={{
                        pathname: "/printing/edit_many_docs",
                        search: `?ids_docs=${selectedIds.join(",")}`,
                    }}
                    state={{ from: location.pathname + location.search }}
                    onClick={(e) => {
                        if (selectedIds.length === 0) e.preventDefault();
                    }}
                    className={`ml-auto flex items-center gap-[0.8vw] bg-[#246CE2] rounded-full py-[1.2vh] px-[2.2vw] text-[1.7vh] font-bold transition-opacity ${
                        selectedIds.length === 0
                            ? "opacity-40 pointer-events-none"
                            : "opacity-100"
                    }`}
                >
                    {t("listDoc.continue")}
                    <svg
                        className="w-[2vh] h-[2vh]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                </Link>
            </motion.div>
        </div>
    );
}

export default ListDoc;
